import {
  type ClaimLegalStepName,
  legalStepBadgeClasses,
  legalSteps,
} from "@/features/claims/lib/legal-steps";
import { cn } from "@/lib/utils";

/** Static dot classes per step (Tailwind can't resolve dynamic `bg-{color}-500`). */
const legalStepDotClasses: Record<ClaimLegalStepName, { halo: string; dot: string }> = {
  CLAIM_VALIDATION: { halo: "bg-teal-500/20", dot: "bg-teal-500" },
  UPCLAIM: { halo: "bg-blue-500/20", dot: "bg-blue-500" },
  CONCILIATION: { halo: "bg-amber-500/20", dot: "bg-amber-500" },
  COURT: { halo: "bg-red-500/20", dot: "bg-red-500" },
  BAILIFF: { halo: "bg-indigo-500/20", dot: "bg-indigo-500" },
  CANCELLED: { halo: "bg-gray-500/20", dot: "bg-gray-500" },
  LOST: { halo: "bg-gray-500/20", dot: "bg-gray-500" },
  WAITING_FOR_MONEY: { halo: "bg-violet-500/20", dot: "bg-violet-500" },
  MONEY_RECEIVED: { halo: "bg-green-500/20", dot: "bg-green-500" },
  PAYMENT_SENT: { halo: "bg-lime-500/20", dot: "bg-lime-500" },
};

export function isKnownLegalStep(name: string): name is ClaimLegalStepName {
  return name in legalSteps;
}

/** Port of DotLegalStep.vue. */
export function DotLegalStep({ name }: { name: ClaimLegalStepName }) {
  const classes = legalStepDotClasses[name];
  return (
    <div className={cn("flex-none rounded-full p-1", classes.halo)}>
      <div className={cn("h-1.5 w-1.5 rounded-full", classes.dot)} />
    </div>
  );
}

interface BadgeLegalStepProps {
  name: ClaimLegalStepName;
  dot?: boolean;
  additionalText?: string;
}

/** Port of BadgeLegalStep.vue (dot and plain variants). */
export function BadgeLegalStep({ name, dot = false, additionalText }: BadgeLegalStepProps) {
  if (dot) {
    return (
      <span className="inline-flex items-center gap-x-1.5 rounded-full py-1 text-xs font-medium text-gray-900">
        <DotLegalStep name={name} />
        {legalSteps[name].displayText}
        {additionalText}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md px-2.5 py-1 text-xs font-medium capitalize ring-1 ring-inset",
        legalStepBadgeClasses[name],
      )}
    >
      {legalSteps[name].displayText}
      {additionalText}
    </span>
  );
}
